import { config } from './config.js';
import { twinApex } from './path';

export function getLineIntersection(line1, line2) {
  const { x1: x1, y1: y1, x2: x2, y2: y2 } = line1;
  const { x1: x3, y1: y3, x2: x4, y2: y4 } = line2;

  const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);

  if (Math.abs(denom) < 0.0001) {
    return null;
  }

  const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;
  const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denom;

  if (t >= 0 && t <= 1 && u >= 0 && u <= 1) {
    return {
      x: x1 + t * (x2 - x1),
      y: y1 + t * (y2 - y1),
      t1: t,
      t2: u
    };
  }

  return null;
}

export function createPathWithHooks(line, intersections) {
  if (!config.ENABLE_LINE_INTERSECTION_HOOKS || intersections.length === 0) {
    return `M ${line.x1},${line.y1} L ${line.x2},${line.y2}`;
  }

  const sorted = intersections
    .filter((int) => int.t > 0.05 && int.t < 0.95)
    .sort((a, b) => a.t - b.t);

  if (sorted.length === 0) {
    return `M ${line.x1},${line.y1} L ${line.x2},${line.y2}`;
  }

  const hookSize = config.LINE_HOOK_SIZE;
  const hookRadius = config.LINE_HOOK_RADIUS;
  const dx = line.x2 - line.x1;
  const dy = line.y2 - line.y1;
  const length = Math.sqrt(dx * dx + dy * dy);

  if (length === 0) return `M ${line.x1},${line.y1}`;

  const dirX = dx / length;
  const dirY = dy / length;

  const perpX = -dirY;
  const perpY = dirX;

  let path = `M ${line.x1},${line.y1}`;

  sorted.forEach((intersection) => {
    const { x, y } = intersection;

    const beforeX = x - (dirX * hookSize) / 2;
    const beforeY = y - (dirY * hookSize) / 2;
    const afterX = x + (dirX * hookSize) / 2;
    const afterY = y + (dirY * hookSize) / 2;

    const ctrlX = x + perpX * hookRadius;
    const ctrlY = y + perpY * hookRadius;

    path += ` L ${beforeX},${beforeY}`;

    path += ` Q ${ctrlX},${ctrlY} ${afterX},${afterY}`;
  });

  path += ` L ${line.x2},${line.y2}`;

  return path;
}

export function detectAllIntersections(edges, getNodeById) {
  const intersectionMap = new Map();

  const lines = edges.map((edge, index) => {
    const source = getNodeById(edge.from);
    const target = getNodeById(edge.to);
    return {
      index,
      edge,
      x1: source.x,
      y1: source.y,
      x2: target.x,
      y2: target.y
    };
  });

  for (let i = 0; i < lines.length; i++) {
    for (let j = i + 1; j < lines.length; j++) {
      const intersection = getLineIntersection(lines[i], lines[j]);

      if (intersection) {
        if (!intersectionMap.has(i)) {
          intersectionMap.set(i, []);
        }
        if (!intersectionMap.has(j)) {
          intersectionMap.set(j, []);
        }

        intersectionMap.get(i).push({
          x: intersection.x,
          y: intersection.y,
          t: intersection.t1,
          withLine: j
        });

        intersectionMap.get(j).push({
          x: intersection.x,
          y: intersection.y,
          t: intersection.t2,
          withLine: i
        });
      }
    }
  }

  return intersectionMap;
}

// opts.drawn (Canvas path-rebuild only): return the RENDERED geometry instead
// of the SCORED geometry. The two differ exactly where a score-the-fiction
// split applies — a TGCLOUD-1577 blocked spouse bar (drawn straight at row
// level, scored on its legacy lane via scoreLaneY) and a TGCLOUD-1578
// de-merged sibling line (drawn at the offset laneY, scored at the legacy
// midY via laneDrawnOnly). Scoring/arc-map callers omit opts, keeping every
// crossing count byte-identical to the legacy geometry; the hook path builder
// passes {drawn:true} so rebuilt paths stay on the ink.
export function decomposePathIntoSegments(edge, nodesById, opts) {
  const drawn = !!(opts && opts.drawn);
  const fromNode = nodesById.get(edge.from);
  const toNode = nodesById.get(edge.to);

  // TGCLOUD-1741: a twin-group edge (the fan) is now FIRST-CLASS detector geometry.
  // Before this, a fan produced no segments at all, so a splayed twin set crossing
  // another family's drop scored ZERO — the optimiser could split a twin pair for
  // free and nothing (corpus ratchet, crossingRefine, best-of-both, beacon) could
  // ever see or repair it. The geometry is derived from NODE POSITIONS via the same
  // `twinApex` formula `twinGroupPath` renders from, so detector and ink stay in
  // lock-step by construction (the Round-27 pattern) — and it works in scoring mode,
  // where the edge carries `from`/`twinIds` but no `d`.
  if (edge.kind === 'twin-group') {
    if (!fromNode) return [];
    const twinNodes = (edge.twinIds || [])
      .map((pid) => nodesById.get(pid))
      .filter((n) => n && Number.isFinite(n.x) && Number.isFinite(n.y));
    const apex = twinApex({ x: fromNode.x, y: fromNode.y }, twinNodes);
    if (!apex) return [];
    const { x: ux, y: uy } = fromNode;
    const edgeId =
      edge.from +
      '-twins-' +
      [...(edge.twinIds || [])].sort((a, b) => a - b).join('-');
    const twinSegments = [];
    // Drop from the union to the meeting height (same 1px float-merge tolerance
    // as the child branch).
    twinSegments.push({
      x1: ux,
      y1: uy,
      x2: ux,
      y2: apex.y,
      slope: Infinity,
      type: 'vertical',
      edgeId,
      edgeKind: 'twin-group',
      segmentIndex: 0
    });
    if (Math.abs(ux - apex.x) >= 1) {
      twinSegments.push({
        x1: ux,
        y1: apex.y,
        x2: apex.x,
        y2: apex.y,
        slope: 0,
        type: 'horizontal',
        edgeId,
        edgeKind: 'twin-group',
        segmentIndex: 1
      });
    }
    // One slanted line per positioned twin. A middle member of a 3+ set sits at
    // the apex x and drops vertically; everyone else gets a true DIAGONAL with its
    // real slope. Diagonals join the broad-phase like wide bars (registered across
    // their bbox cells) and can only ever pair with a VERTICAL — see the explicit
    // one-vertical rule in processSegmentPair.
    twinNodes.forEach((twin, i) => {
      const dx = twin.x - apex.x;
      const dy = twin.y - apex.y;
      if (Math.abs(dx) < 1) {
        twinSegments.push({
          x1: apex.x,
          y1: apex.y,
          x2: apex.x,
          y2: twin.y,
          slope: Infinity,
          type: 'vertical',
          edgeId,
          edgeKind: 'twin-group',
          segmentIndex: 2 + i
        });
      } else {
        twinSegments.push({
          x1: apex.x,
          y1: apex.y,
          x2: twin.x,
          y2: twin.y,
          slope: dy / dx,
          type: 'diagonal',
          edgeId,
          edgeKind: 'twin-group',
          segmentIndex: 2 + i
        });
      }
    });
    return twinSegments;
  }

  if (!fromNode || !toNode) return [];

  const segments = [];

  if (edge.kind === 'spouse') {
    // STRETCHED containment-bridge marriage routed on a lane below the row (edge.laneY):
    // down from each partner to the lane, across at laneY, up to the other partner —
    // the SAME route spousePath draws, so the rendered line and this detector geometry
    // stay in lock-step (mirrors the over-the-top child decomposition below).
    //
    // TGCLOUD-1577 (`scoreLaneY`): when lane ROUTING is off, a blocked bar is
    // DRAWN straight middle-to-middle (Kathryn's ruling — it may intersect the
    // people between the partners, "user can then make decisions"), but it is
    // still SCORED on the legacy lane geometry. This keeps every crossing
    // count — and therefore every Pareto decision, search basin and settled
    // position — byte-identical to the lane-routing world ("people jumping
    // around on the pedigree … we must avoid. Predictability is key."). The
    // straight bar's own drawn intersections are licensed by the ruling and
    // deliberately invisible to the optimiser; hops arising from the
    // fictional lane are tagged noArc below so nothing is drawn in the empty
    // band where the lane used to be.
    const scoreLy = drawn ? edge.laneY : edge.laneY ?? edge.scoreLaneY;
    if (scoreLy != null && Math.abs(fromNode.x - toNode.x) >= 1) {
      const ly = scoreLy;
      const id = edge.from + '-' + edge.to;
      const fictional = edge.laneY == null ? { fictionalLane: true } : {};
      segments.push({
        x1: fromNode.x,
        y1: fromNode.y,
        x2: fromNode.x,
        y2: ly,
        slope: Infinity,
        type: 'vertical',
        edgeId: id,
        edgeKind: 'spouse',
        segmentIndex: 0,
        ...fictional
      });
      segments.push({
        x1: fromNode.x,
        y1: ly,
        x2: toNode.x,
        y2: ly,
        slope: 0,
        type: 'horizontal',
        edgeId: id,
        edgeKind: 'spouse',
        segmentIndex: 1,
        ...fictional
      });
      segments.push({
        x1: toNode.x,
        y1: ly,
        x2: toNode.x,
        y2: toNode.y,
        slope: Infinity,
        type: 'vertical',
        edgeId: id,
        edgeKind: 'spouse',
        segmentIndex: 2,
        ...fictional
      });
      return segments;
    }
    // NB slope/type are hardcoded horizontal even when the partners sit at different y
    // (a diagonal marriage line — e.g. an extra-spouse sub-row, or the displaced-sibship
    // lift's tilted 25-35 line). This is fine against VERTICAL drops (slope Infinity wins
    // the steeper-segment hook either way) and the actual crossing point is computed from
    // the true endpoints below — but a diagonal-vs-diagonal mid-span crossing is a blind
    // spot. Pre-existing (CXGEN already ships diagonal spouse edges); compute the real
    // slope here if a future change produces two diagonal spouse edges in one family.
    segments.push({
      x1: fromNode.x,
      y1: fromNode.y,
      x2: toNode.x,
      y2: toNode.y,
      slope: 0,
      type: 'horizontal',
      edgeId: edge.from + '-' + edge.to,
      edgeKind: 'spouse'
    });
  } else if (edge.kind === 'child') {
    // TGCLOUD-1577 scoring parity: a blocked union drawn at row level carries
    // scoreLaneY (where it sat under lane routing) — its drops are scored
    // from that legacy top so crossing counts match the lane-routing world.
    const { x: ux } = fromNode;
    const uy = drawn ? fromNode.y : fromNode.scoreLaneY ?? fromNode.y;
    const { x: cx, y: cy } = toNode;

    // TGCLOUD-1741 (relates TGCLOUD-1671 — the G-1-00368 curls): wherever the
    // SCORED geometry departs from the ink, the departure must be DECLARED, or a
    // crossing recorded against the displaced position draws an arc where nothing
    // crosses. Two displacements exist for child edges, and they compose:
    //   * a blocked union's scoreLaneY moves the scored drop-top (and with it the
    //     scored mid-Y elbow — 141-45's children's bar scored at 5610, inked 5400);
    //   * a de-merged (laneDrawnOnly) edge's bar is inked at laneY but scored at
    //     the legacy mid-Y.
    // Scored VERTICALS carry `drawnYRange` (the inked y-extent at the same x) so
    // processSegmentPair can noArc a record landing off it; a scored HORIZONTAL at
    // a y the ink never occupies is tagged fictionalLane outright, so crossings
    // WITH it are suppressed on the other edge too. Counts are untouched — this is
    // tagging only, the 1577/1578 score-the-fiction contract completed.
    const uyDrawn = fromNode.y;

    // Treat a union and child that share an x as a SINGLE straight drop. Layout
    // arithmetic leaves sub-pixel float error (e.g. 6600 vs 6599.999999999999), so
    // an exact `===` would split a genuinely-vertical drop into two stacked
    // verticals around a degenerate mid-Y elbow — and a line crossing there would
    // then hook BOTH halves (the spurious "double hop"). Real dog-legs are at least
    // a symbol-width apart, so a 1px tolerance can't merge distinct columns.
    if (Math.abs(ux - cx) < 1) {
      segments.push({
        x1: ux,
        y1: uy,
        x2: ux,
        y2: cy,
        slope: Infinity,
        type: 'vertical',
        edgeId: edge.from + '-' + edge.to,
        edgeKind: 'child',
        ...(!drawn && Math.abs(uy - uyDrawn) >= 1
          ? { drawnYRange: [uyDrawn, cy] }
          : {})
      });
    } else {
      // An OVER-THE-TOP child edge raises the horizontal sibling-line to edge.laneY
      // (above the union) instead of the mid-Y elbow; childPath draws the SAME route, so
      // the rendered line and this detector geometry stay in lock-step.
      // laneDrawnOnly (TGCLOUD-1578 sibling-line de-merge): the offset bar is
      // DRAWN at edge.laneY but SCORED at the legacy midY, so the optimiser
      // and every settled position are byte-identical with the de-merge on or
      // off; the displaced horizontal is tagged fictional below so crossings with
      // it never draw an arc at the un-inked scored y.
      const laneApplies = edge.laneY != null && (drawn || !edge.laneDrawnOnly);
      const midY = laneApplies ? edge.laneY : (uy + cy) / 2;
      // The y the INK's elbow actually sits at (drawn-mode laneApplies is
      // unconditional when laneY is set).
      const midYDrawn = edge.laneY != null ? edge.laneY : (uyDrawn + cy) / 2;
      const midYFictional = !drawn && Math.abs(midY - midYDrawn) >= 1;
      const seg0Displaced =
        !drawn &&
        (Math.abs(uy - uyDrawn) >= 1 || Math.abs(midY - midYDrawn) >= 1);

      segments.push({
        x1: ux,
        y1: uy,
        x2: ux,
        y2: midY,
        slope: Infinity,
        type: 'vertical',
        edgeId: edge.from + '-' + edge.to,
        edgeKind: 'child',
        segmentIndex: 0,
        ...(seg0Displaced ? { drawnYRange: [uyDrawn, midYDrawn] } : {})
      });

      segments.push({
        ...(midYFictional ? { fictionalLane: true } : {}),
        x1: ux,
        y1: midY,
        x2: cx,
        y2: midY,
        slope: 0,
        type: 'horizontal',
        edgeId: edge.from + '-' + edge.to,
        edgeKind: 'child',
        segmentIndex: 1
      });

      segments.push({
        x1: cx,
        y1: midY,
        x2: cx,
        y2: cy,
        slope: Infinity,
        type: 'vertical',
        edgeId: edge.from + '-' + edge.to,
        edgeKind: 'child',
        segmentIndex: 2,
        ...(midYFictional ? { drawnYRange: [midYDrawn, cy] } : {})
      });
    }
  }

  return segments;
}

// The real (rendered) node positions an edge connects: a spouse edge touches its
// two partners; a child edge touches the child and the union's two partners
// (parsed from `union-A-B`) — NOT the synthetic mid-Y elbow corners. Used to
// suppress a hook only when a crossing sits on an actual symbol, and to tell
// whether two edges are STRUCTURALLY CONNECTED (a junction, not a cross-over).
function edgeNodeInfo(edge, nodesById) {
  const pids = new Set();
  const add = (id) => id != null && pids.add(id);
  if (edge.kind === 'spouse') {
    add(edge.from);
    add(edge.to);
  } else if (edge.kind === 'child' || edge.kind === 'twin-group') {
    // A twin-group edge touches its union's partners and every positioned twin —
    // so the near-node test suppresses junction touches at the parents' symbols
    // and at each twin's own symbol (where its marriage bar or descent starts).
    if (edge.kind === 'twin-group') {
      (edge.twinIds || []).forEach(add);
    } else {
      add(edge.to);
    }
    if (typeof edge.from === 'string' && edge.from.startsWith('union-')) {
      const parts = edge.from.slice('union-'.length).split('-');
      parts.forEach((p) => add(/^\d+$/.test(p) ? Number(p) : p));
    } else {
      add(edge.from); // single-parent edge: from is the parent person
    }
  }
  const points = [];
  for (const id of pids) {
    const n = nodesById.get(id);
    if (n) points.push({ x: n.x, y: n.y });
  }
  return { pids, points, kind: edge.kind, from: edge.from };
}

// Two edges are connected (their crossing is a junction, not a cross-over) when
// they share any node — siblings sharing a union, a child meeting its union's
// spouse line, a shared/cross-married child, etc.
//
// EXCEPTION (detector honesty, TGCLOUD-1468 follow-up): two TWO-PARENT (union) child
// edges from DIFFERENT unions are NOT a junction merely because the unions share a
// CO-PARENT (a hub married into both families). Their drops and sibship bars can
// genuinely cross — a step-child's drop cutting through the other union's sibship bar —
// and that crossing must both SCORE (the optimiser was blind to it) and get a HOP-OVER
// (the renderer reads the same map). The geometry + near-node test downstream still
// suppresses a true meeting AT the shared symbol. Single-parent (solo/half-sib) child
// edges keep the junction skip — they intentionally meet their coupled parent's sibship
// at the edge (the C1366 convention).
function edgesAreConnected(infoA, infoB) {
  // TGCLOUD-1741: a twin-group fan is a union-anchored DESCENT edge exactly like a
  // two-parent child edge, so the TGCLOUD-1468 honesty exception applies to it too:
  // descent edges from DIFFERENT unions are not a junction merely because the unions
  // share a co-parent (a hub married into both families). Without this, the DZ fan
  // from union-2-3 and the union-10-3→9 drop would "share" hub 3 and the fan's real
  // crossing over the half-sib's drop would be silently skipped — the exact G-19-01534
  // defect this work makes visible. Same-union pairs still share the full union id and
  // stay junctions; single-parent descent (numeric `from`) keeps the C1366 skip.
  const isUnionDescent = (info) =>
    (info.kind === 'child' || info.kind === 'twin-group') &&
    typeof info.from === 'string' &&
    info.from.startsWith('union-');
  const differentUnions =
    isUnionDescent(infoA) && isUnionDescent(infoB) && infoA.from !== infoB.from;
  if (differentUnions) return false;
  for (const id of infoA.pids) if (infoB.pids.has(id)) return true;
  return false;
}

// Test ONE unordered segment pair and record a hook into `intersectionMap` if they cross.
// seg1/seg2 MUST be passed in ascending allSegments order (seg1 before seg2) — exactly as
// the all-pairs i<j loop would — so the recorded geometry (including the `t` parameter,
// which comes from getLineIntersection's argument order) is byte-identical regardless of
// which broad-phase fed the pair. This is the single source of truth for the inner logic;
// both detectAllSegmentIntersections (grid) and ...AllPairs (oracle) call it.
function processSegmentPair(seg1, seg2, infoByEdgeId, intersectionMap, drawn) {
  if (seg1.edgeId === seg2.edgeId) return;

  // Structurally-connected edges (sharing a person/union — e.g. two siblings
  // off one union, or a child meeting its union's spouse line) MEET at a
  // junction; that is not a cross-over, so it never gets a hop. Skipping these
  // explicitly lets the near-node test below ignore the synthetic mid-Y elbow
  // corners (and so a genuine cross-over sitting at the shared generation
  // mid-Y — e.g. a dog-leg over an unrelated family's drop — keeps its hop).
  const infoA = infoByEdgeId.get(seg1.edgeId);
  const infoB = infoByEdgeId.get(seg2.edgeId);
  if (infoA && infoB && edgesAreConnected(infoA, infoB)) return;

  const intersection = getLineIntersection(
    { x1: seg1.x1, y1: seg1.y1, x2: seg1.x2, y2: seg1.y2 },
    { x1: seg2.x1, y1: seg2.y1, x2: seg2.x2, y2: seg2.y2 }
  );

  if (!intersection) return;

  // Suppress a hook only when the crossing sits on an actual rendered
  // symbol (a person/union node of either edge), NOT near a mid-Y elbow
  // corner — those are not symbols, and a dog-leg crossing there needs a hop.
  const nodePts = [
    ...((infoA && infoA.points) || []),
    ...((infoB && infoB.points) || [])
  ];
  const tooClose = nodePts.some(
    (p) => Math.hypot(intersection.x - p.x, intersection.y - p.y) < 50
  );

  if (tooClose) return;

  // DRAWN-MODE ENDPOINT RULE (TGCLOUD-1741/1671): in the render map an
  // intersection sitting AT a segment end (≤1px) is a DEPARTURE, not a
  // crossover — a drop leaving downward from a straight row-level marriage bar
  // (the Kathryn-licensed 1577 intersections), or a bar ending on a line. The
  // ink shows a T, and a T never hops. Real crossings sit well inside both
  // segments (≥ a hook-width). Scored mode keeps every endpoint touch — the
  // optimiser's counts must stay byte-identical to the fiction.
  if (drawn) {
    const nearEnd = (s) =>
      Math.hypot(intersection.x - s.x1, intersection.y - s.y1) <= 1 ||
      Math.hypot(intersection.x - s.x2, intersection.y - s.y2) <= 1;
    if (nearEnd(seg1) || nearEnd(seg2)) return;
  }

  // ONE-VERTICAL RULE (TGCLOUD-1741): a pair is scored only when one segment is
  // vertical. Twin fans introduced true DIAGONAL segments; a diagonal×horizontal
  // (or diagonal×diagonal) crossing has no renderable hop under the house
  // convention (the arc always rides a vertical) AND the grid broad-phase only
  // ever pairs verticals against spanners — making the rule explicit here keeps
  // the all-pairs oracle byte-identical to the grid (lineIntersectionParity)
  // instead of silently diverging on the first family with a wide fan over a bar.
  if (seg1.type !== 'vertical' && seg2.type !== 'vertical') return;

  // The STEEPER segment carries the hook: a VERTICAL line arcs over a
  // horizontal (or a fan diagonal — the standard pedigree convention: never
  // bump the flatter line). Two verticals can't cross cleanly — the equal-slope
  // case below skips them.
  // If the crossing lands at the vertical's corner (a same-generation drop
  // meeting another family's bar at the shared mid-Y), the path builder slides
  // the arc just inside the segment, so it still hops on the vertical.
  let targetSeg, otherSlope;
  if (Math.abs(seg1.slope) > Math.abs(seg2.slope)) {
    targetSeg = seg1;
    otherSlope = seg2.slope;
  } else if (Math.abs(seg2.slope) > Math.abs(seg1.slope)) {
    targetSeg = seg2;
    otherSlope = seg1.slope;
  } else {
    return; // same orientation — not a vertical-over-horizontal crossing
  }

  // A twin-group edge has no `to` — its segments' edgeId (union + sorted twin pids)
  // is the stable unique key, and keeps two twin sets of one union from colliding.
  const edgeKey =
    targetSeg.parentEdge.kind === 'twin-group'
      ? targetSeg.edgeId
      : targetSeg.parentEdge.from + '-' + targetSeg.parentEdge.to;

  if (!intersectionMap.has(edgeKey)) {
    intersectionMap.set(edgeKey, []);
  }

  const otherSeg = targetSeg === seg1 ? seg2 : seg1;

  // TGCLOUD-1741 (relates TGCLOUD-1671 — false hop-overs on Desktop imports): the
  // missing half of the 1577/1578 score-the-fiction contracts. A scored VERTICAL
  // can be displaced from its ink — a de-merged (laneDrawnOnly) drop is scored
  // from the legacy midY but inked from laneY; a blocked union's drop is scored
  // from scoreLaneY but inked from the union's real row. decompose stamps such a
  // segment with `drawnYRange` (its inked y-extent at the same x); a record whose
  // crossing lies outside it must still COUNT (scoring parity) but must not draw —
  // the arc would land on a stretch of the drop the ink never reaches. Verticals
  // are always the arc carrier (nothing is steeper), so the target-side test is
  // complete; a displaced HORIZONTAL is fictionalLane-tagged in decompose and
  // suppressed via the otherSeg term below.
  let offInkLaneVertical = false;
  if (targetSeg.drawnYRange) {
    const [ra, rb] = targetSeg.drawnYRange;
    offInkLaneVertical =
      intersection.y < Math.min(ra, rb) - 1 ||
      intersection.y > Math.max(ra, rb) + 1;
  }

  intersectionMap.get(edgeKey).push({
    x: intersection.x,
    y: intersection.y,
    t: targetSeg === seg1 ? intersection.t1 : intersection.t2,
    segmentIndex:
      targetSeg.segmentIndex !== undefined ? targetSeg.segmentIndex : 0,
    segment: targetSeg,
    otherSlope: otherSlope,
    // TGCLOUD-1577: a crossing with a FICTIONAL lane (a blocked bar scored on
    // the legacy lane geometry but DRAWN straight at row level) counts for
    // scoring parity, but must not draw an arc — the lane it crosses is not
    // rendered, so the hop would float in empty space below the row.
    ...(seg1.fictionalLane ||
    seg2.fictionalLane ||
    otherSeg.fictionalLane ||
    offInkLaneVertical
      ? { noArc: true }
      : {})
  });
}

// Decompose every edge into axis-aligned segments + cache each edge's node info. Shared by
// the grid detector and the all-pairs oracle so both see identical segment data (incl. the
// `_idx` ascending order the all-pairs i<j loop relies on).
function buildSegments(edges, nodesById, opts) {
  const allSegments = [];
  const infoByEdgeId = new Map();
  edges.forEach((edge) => {
    const segments = decomposePathIntoSegments(edge, nodesById, opts);
    if (segments.length) {
      infoByEdgeId.set(segments[0].edgeId, edgeNodeInfo(edge, nodesById));
    }
    for (const seg of segments) {
      allSegments.push({ ...seg, parentEdge: edge, _idx: allSegments.length });
    }
  });
  return { allSegments, infoByEdgeId };
}

// Crossing detector — spatial-grid broad-phase, O(S) build + ~O(S + candidates) query
// instead of the O(S²) all-pairs loop. ONLY a vertical×horizontal pair can ever record a
// hook (a same-orientation pair hits the equal-slope `return` in processSegmentPair), and a
// pair whose bounding boxes don't overlap can't intersect. A uniform grid keyed by
// (col,row) yields exactly the pairs that share a cell — a sound SUPERSET of bbox-overlap,
// so no true crossing is ever missed. Every candidate is run through the SAME
// processSegmentPair in ascending _idx order, so the result is byte-identical to
// detectAllSegmentIntersectionsAllPairs (verified across the corpus in
// lineIntersectionParity.test.js). Used on the hot path (reorder search scores it hundreds
// of times) and for rendering hooks.
export function detectAllSegmentIntersections(edges, nodesById, opts) {
  const intersectionMap = new Map();
  const drawn = !!(opts && opts.drawn);
  const { allSegments, infoByEdgeId } = buildSegments(edges, nodesById, opts);

  const verticals = [];
  const horizontals = [];
  for (const s of allSegments) {
    if (s.type === 'vertical') verticals.push(s);
    else horizontals.push(s);
  }
  if (!verticals.length || !horizontals.length) return intersectionMap;

  // Cell ≈ one node gap (the natural spacing of drops/bars). Degenerate inputs (tiny/zero
  // gap) just make the grid coarser → falls back toward all-pairs, never incorrect.
  const CELL = config.NODE_GAP > 0 ? config.NODE_GAP : 1;
  const colOf = (x) => Math.floor(x / CELL);
  const rowOf = (y) => Math.floor(y / CELL);

  // Register each horizontal in every grid cell its bounding box overlaps. Most bars are
  // narrow (≤ a couple of cells); the rare wide bridge touches more but there are few.
  const grid = new Map(); // "col:row" -> horizontal segments
  for (const h of horizontals) {
    const c0 = colOf(Math.min(h.x1, h.x2));
    const c1 = colOf(Math.max(h.x1, h.x2));
    const r0 = rowOf(Math.min(h.y1, h.y2));
    const r1 = rowOf(Math.max(h.y1, h.y2));
    for (let c = c0; c <= c1; c++) {
      for (let r = r0; r <= r1; r++) {
        const k = c + ':' + r;
        let arr = grid.get(k);
        if (!arr) grid.set(k, (arr = []));
        arr.push(h);
      }
    }
  }

  // Each vertical occupies a single column over a few rows; gather the horizontals sharing
  // those cells (dedup — a wide/diagonal bar can sit in several of the vertical's cells).
  const seen = new Set();
  for (const v of verticals) {
    const col = colOf(v.x1); // x1 === x2 for a vertical
    const r0 = rowOf(Math.min(v.y1, v.y2));
    const r1 = rowOf(Math.max(v.y1, v.y2));
    seen.clear();
    for (let r = r0; r <= r1; r++) {
      const arr = grid.get(col + ':' + r);
      if (!arr) continue;
      for (const h of arr) {
        if (seen.has(h._idx)) continue;
        seen.add(h._idx);
        const seg1 = v._idx < h._idx ? v : h;
        const seg2 = v._idx < h._idx ? h : v;
        processSegmentPair(seg1, seg2, infoByEdgeId, intersectionMap, drawn);
      }
    }
  }

  return intersectionMap;
}

// Reference oracle: the original O(S²) all-pairs detector. Retained as the byte-identical
// correctness baseline for detectAllSegmentIntersections (see lineIntersectionParity.test.js)
// and as a safe fallback. Not on any hot path — do not use for scoring/rendering.
export function detectAllSegmentIntersectionsAllPairs(edges, nodesById, opts) {
  const intersectionMap = new Map();
  const drawn = !!(opts && opts.drawn);
  const { allSegments, infoByEdgeId } = buildSegments(edges, nodesById, opts);
  for (let i = 0; i < allSegments.length; i++) {
    for (let j = i + 1; j < allSegments.length; j++) {
      processSegmentPair(
        allSegments[i],
        allSegments[j],
        infoByEdgeId,
        intersectionMap,
        drawn
      );
    }
  }
  return intersectionMap;
}

export function createPathWithHooksAtSegments(
  fromNode,
  toNode,
  segments,
  intersections
) {
  if (
    !config.ENABLE_LINE_INTERSECTION_HOOKS ||
    !intersections ||
    intersections.length === 0
  ) {
    if (segments && segments.length > 0 && segments[0].type === 'horizontal') {
      return `M ${fromNode.x},${fromNode.y} L ${toNode.x},${toNode.y}`;
    } else if (fromNode.x === toNode.x) {
      return `M ${fromNode.x},${fromNode.y} L ${toNode.x},${toNode.y}`;
    } else {
      const midY = (fromNode.y + toNode.y) / 2;
      return `M ${fromNode.x},${fromNode.y} L ${fromNode.x},${midY} L ${toNode.x},${midY} L ${toNode.x},${toNode.y}`;
    }
  }

  const hookSize = config.LINE_HOOK_SIZE;
  const hookRadius = config.LINE_HOOK_RADIUS;

  const segmentIntersections = {};
  intersections.forEach((int) => {
    // TGCLOUD-1577: crossings with a FICTIONAL lane exist for scoring parity
    // only — nothing is drawn where the lane used to be, so no arc.
    if (int.noArc) return;
    const idx = int.segmentIndex || 0;
    if (!segmentIntersections[idx]) {
      segmentIntersections[idx] = [];
    }
    segmentIntersections[idx].push(int);
  });

  let path = `M ${fromNode.x},${fromNode.y}`;

  segments.forEach((seg, idx) => {
    const ints = segmentIntersections[idx] || [];

    if (ints.length === 0) {
      path += ` L ${seg.x2},${seg.y2}`;
    } else {
      const sorted = ints.sort((a, b) => {
        const dist1 = Math.hypot(a.x - seg.x1, a.y - seg.y1);
        const dist2 = Math.hypot(b.x - seg.x1, b.y - seg.y1);
        return dist1 - dist2;
      });

      // A sibling bar is drawn as several overlapping horizontal child-edge
      // segments, so a crossing line can intersect two or three of them at the
      // SAME point. Each would emit its own hook; two hooks at (nearly) the same
      // spot make the path backtrack straight through the arc — leaving a solid
      // line on top of the hop. Collapse hooks closer than one hook-width: it is
      // visually a single crossing, so it gets a single arc.
      let lastAlong = null;

      sorted.forEach((int) => {
        const isVertical = seg.type === 'vertical';

        const along = isVertical ? int.y : int.x;
        if (lastAlong !== null && Math.abs(along - lastAlong) < hookSize) {
          return;
        }
        lastAlong = along;

        let beforeX, beforeY, afterX, afterY, ctrlX, ctrlY;

        if (isVertical) {
          // Keep the whole arc inside the segment. When the crossing sits at a
          // corner (a same-generation drop meets another family's bar at the shared
          // mid-Y), slide the arc just inside the segment rather than overshooting
          // past the bend — the hop stays on the vertical, drawn cleanly.
          const segMinY = Math.min(seg.y1, seg.y2);
          const segMaxY = Math.max(seg.y1, seg.y2);
          const half = Math.min(hookSize / 2, (segMaxY - segMinY) / 2);
          const cy = Math.max(segMinY + half, Math.min(segMaxY - half, int.y));
          beforeY = cy - half;
          afterY = cy + half;
          beforeX = int.x;
          afterX = int.x;
          ctrlX = int.x + hookRadius;
          ctrlY = cy;
        } else {
          beforeX = int.x - hookSize / 2;
          afterX = int.x + hookSize / 2;
          beforeY = int.y;
          afterY = int.y;
          ctrlX = int.x;
          ctrlY = int.y + hookRadius;
        }

        path += ` L ${beforeX},${beforeY}`;

        path += ` Q ${ctrlX},${ctrlY} ${afterX},${afterY}`;
      });

      path += ` L ${seg.x2},${seg.y2}`;
    }
  });

  return path;
}
